import {
    Avatar,
    Badge,
    Body1,
    Button,
    Caption1,
    Divider,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    OverlayDrawer,
    Spinner,
    Subtitle1,
    Subtitle2,
    Tag,
    Title3,
    tokens,
} from "@fluentui/react-components";
import {
    ArrowResetRegular,
    BookOpenRegular,
    BuildingMultipleRegular,
    CheckmarkCircle20Filled,
    Comment20Regular,
    Dismiss24Regular,
    DocumentRibbonRegular,
    DocumentTextRegular,
    MoneyRegular,
    PersonRegular,
    Search20Regular,
    Warning20Filled,
} from "@fluentui/react-icons";
import moment from "moment";
import { useEffect } from "react";
import { connect } from "react-redux";

import * as ComponentFunctions from "./Debt.Details.Functions";

const DetailField = ({
    label,
    value,
}: {
    label: string;
    value: React.ReactNode;
}) => {
    const styles = ComponentFunctions.useStyles();
    return (
        <div className={styles.field}>
            <span className={styles.fieldLabel}>{label}</span>
            <span className={styles.fieldValue}>{value}</span>
        </div>
    );
};

const PartyCard = ({
    label,
    person,
    identifier,
}: {
    label: string;
    person: { businessName?: string } | undefined;
    identifier: string;
}) => {
    const styles = ComponentFunctions.useStyles();
    return (
        <div className={styles.partyRow}>
            <Avatar
                name={person?.businessName ?? identifier}
                color="colorful"
                size={40}
                icon={<PersonRegular />}
            />
            <div className={styles.partyMeta}>
                <span className={styles.partyLabel}>{label}</span>
                <span className={styles.partyName} title={person?.businessName ?? identifier}>
                    {person?.businessName ?? "Sin nombre"}
                </span>
                <span className={styles.partyIdentifier} title={identifier}>
                    {identifier}
                </span>
            </div>
        </div>
    );
};

const DebtDetailsComponent = (props: ComponentFunctions.Props) => {
    const styles = ComponentFunctions.useStyles();

    useEffect(() => {
        if (props.isOpen) {
            if (props.DebtId && !props.CurrentDebt) {
                props.GetDebtById(props.DebtId);
            }
            props.GetAllPersons();
            props.GetAllMedicalProtocols();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.DebtId]);

    const debt = props.CurrentDebt;
    const protocol = props.CurrentProtocol;

    const isLoading = props.isLoading && !debt;

    const onVerify = () => {
        if (debt) props.CheckDebtStatus(debt.id);
    };

    const renderStatusBadge = () => {
        if (!debt) return null;
        if (debt.isDebtSettled || debt.pendingAmount <= 0) {
            return (
                <Badge appearance="filled" color="success" icon={<CheckmarkCircle20Filled />}>
                    Saldada
                </Badge>
            );
        }
        if (debt.paidAmount > 0) {
            return (
                <Badge appearance="filled" color="warning">
                    Parcialmente pagada
                </Badge>
            );
        }
        return (
            <Badge appearance="filled" color="danger" icon={<Warning20Filled />}>
                Pendiente
            </Badge>
        );
    };

    const renderSettlementVerification = () => {
        if (!debt) return null;
        const isCheckingThis =
            props.isCheckingDebtStatus && props.checkedDebtStatusId === debt.id;
        const hasResultForThis =
            !props.isCheckingDebtStatus &&
            props.successCheckingDebtStatus &&
            props.checkedDebtStatusId === debt.id;

        if (isCheckingThis) {
            return (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Spinner size="extra-tiny" />
                    <Caption1>Verificando…</Caption1>
                </span>
            );
        }

        if (hasResultForThis) {
            const ok = !!props.debtStatusResult;
            return (
                <Tag
                    appearance="outline"
                    icon={ok ? <CheckmarkCircle20Filled /> : <Warning20Filled />}
                    style={{
                        color: ok
                            ? tokens.colorPaletteGreenForeground1
                            : tokens.colorPaletteRedForeground1,
                    }}
                >
                    {ok ? "Saldada en sistema" : "Con saldo en sistema"}
                </Tag>
            );
        }

        return (
            <Button
                size="small"
                appearance="secondary"
                icon={<Search20Regular />}
                onClick={onVerify}
            >
                Verificar estado
            </Button>
        );
    };

    const renderParties = () => {
        if (!debt) return null;
        return (
            <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>
                    <PersonRegular />
                    <Subtitle2>Partes</Subtitle2>
                </div>
                <div className={styles.grid2}>
                    <PartyCard
                        label="Deudor"
                        person={props.Debtor}
                        identifier={debt.debtor}
                    />
                    <PartyCard
                        label="Acreedor"
                        person={props.Creditor}
                        identifier={debt.creditor}
                    />
                </div>
            </div>
        );
    };

    const renderHeader = () => {
        if (!debt) return null;
        const original = debt.originalAmount || 0;
        const paid = debt.paidAmount || 0;
        const pending = debt.pendingAmount || 0;
        const paidPct = original > 0 ? Math.min(100, (paid / original) * 100) : 0;

        return (
            <div className={styles.headerCard}>
                <div className={styles.headerRow}>
                    <div className={styles.headerTitle}>
                        <Avatar
                            icon={<MoneyRegular />}
                            color="brand"
                            shape="square"
                            size={40}
                        />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                                Deuda #{debt.id}
                            </Caption1>
                            <Subtitle1>
                                {moment(debt.debtDate).format("DD/MM/YYYY")}
                            </Subtitle1>
                        </div>
                    </div>
                    {renderStatusBadge()}
                </div>

                <div>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        Monto original
                    </Caption1>
                    <div className={styles.primaryAmount}>
                        {ComponentFunctions.formatMoney(original, debt.currencyCode)}
                    </div>
                </div>

                <div>
                    <div className={styles.progressTrack}>
                        <div
                            className={styles.progressFillSettled}
                            style={{ width: `${paidPct}%` }}
                        />
                    </div>
                    <div className={styles.progressLegend} style={{ marginTop: 6 }}>
                        <Caption1>
                            Pagado:{" "}
                            <b>{ComponentFunctions.formatMoney(paid, debt.currencyCode)}</b>{" "}
                            ({paidPct.toFixed(1)}%)
                        </Caption1>
                        <Caption1>
                            Pendiente:{" "}
                            <b>{ComponentFunctions.formatMoney(pending, debt.currencyCode)}</b>
                        </Caption1>
                    </div>
                </div>
            </div>
        );
    };

    const renderAmounts = () => {
        if (!debt) return null;
        return (
            <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>
                    <MoneyRegular />
                    <Subtitle2>Valores</Subtitle2>
                </div>
                <div className={styles.grid2}>
                    <DetailField
                        label="Original"
                        value={ComponentFunctions.formatMoney(
                            debt.originalAmount,
                            debt.currencyCode
                        )}
                    />
                    <DetailField
                        label="Moneda"
                        value={
                            <Tag appearance="brand" size="small">
                                {debt.currencyCode}
                            </Tag>
                        }
                    />
                    <DetailField
                        label="Pagado"
                        value={
                            <span style={{ color: tokens.colorPaletteGreenForeground1 }}>
                                {ComponentFunctions.formatMoney(
                                    debt.paidAmount,
                                    debt.currencyCode
                                )}
                            </span>
                        }
                    />
                    <DetailField
                        label="Pendiente"
                        value={
                            <span
                                style={{
                                    color:
                                        debt.pendingAmount > 0
                                            ? tokens.colorPaletteRedForeground1
                                            : tokens.colorPaletteGreenForeground1,
                                }}
                            >
                                {ComponentFunctions.formatMoney(
                                    debt.pendingAmount,
                                    debt.currencyCode
                                )}
                            </span>
                        }
                    />
                    {debt.originalCurrencyCode &&
                        debt.originalCurrencyCode !== debt.currencyCode && (
                            <>
                                <DetailField
                                    label="Importe (moneda origen)"
                                    value={ComponentFunctions.formatMoney(
                                        debt.originalAmountInOriginalCurrency,
                                        debt.originalCurrencyCode
                                    )}
                    />
                                <DetailField
                                    label="Tipo de cambio"
                                    value={
                                        <>
                                            <ArrowResetRegular
                                                style={{
                                                    verticalAlign: "middle",
                                                    marginRight: 4,
                                                }}
                                            />
                                            {debt.currencyExchangeRate?.toFixed(4) ?? "—"}
                                        </>
                                    }
                                />
                            </>
                        )}
                </div>
            </div>
        );
    };

    const renderProtocol = () => {
        if (!debt?.protocolId) return null;
        return (
            <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>
                    <BookOpenRegular />
                    <Subtitle2>Protocolo relacionado</Subtitle2>
                </div>
                {protocol ? (
                    <div className={styles.grid2}>
                        <DetailField
                            label="Título"
                            value={protocol.title || protocol.fullTitle || "—"}
                        />
                        <DetailField
                            label="N° de protocolo"
                            value={protocol.protocolNumber || "—"}
                        />
                        <DetailField
                            label="Laboratorio"
                            value={
                                <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                                    <BuildingMultipleRegular />
                                    {protocol.laboratory?.title ?? "—"}
                                </span>
                            }
                        />
                        <DetailField
                            label="Estado"
                            value={
                                protocol.status?.title ? (
                                    <Tag
                                        appearance="outline"
                                        size="small"
                                        style={{
                                            color: protocol.active
                                                ? tokens.colorPaletteGreenForeground1
                                                : tokens.colorNeutralForeground3,
                                        }}
                                    >
                                        {protocol.status.title}
                                    </Tag>
                                ) : (
                                    "—"
                                )
                            }
                        />
                    </div>
                ) : (
                    <span className={styles.emptyText}>
                        Protocolo #{debt.protocolId} (datos no disponibles)
                    </span>
                )}
            </div>
        );
    };

    const renderSettlement = () => {
        if (!debt) return null;
        const settled = debt.isDebtSettled || debt.pendingAmount <= 0;
        return (
            <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>
                    <DocumentRibbonRegular />
                    <Subtitle2>Estado de liquidación</Subtitle2>
                </div>
                <div className={styles.statusRow}>
                    {settled ? (
                        <Tag
                            appearance="filled"
                            icon={<CheckmarkCircle20Filled />}
                            style={{
                                backgroundColor: tokens.colorPaletteGreenBackground2,
                                color: tokens.colorPaletteGreenForeground1,
                            }}
                        >
                            Saldada
                        </Tag>
                    ) : (
                        <Tag
                            appearance="filled"
                            icon={<Warning20Filled />}
                            style={{
                                backgroundColor: tokens.colorPaletteMarigoldBackground2,
                                color: tokens.colorPaletteMarigoldForeground1,
                            }}
                        >
                            Con saldo pendiente
                        </Tag>
                    )}
                    {renderSettlementVerification()}
                </div>
                {!settled && (
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        Faltan{" "}
                        <b>
                            {ComponentFunctions.formatMoney(
                                debt.pendingAmount,
                                debt.currencyCode
                            )}
                        </b>{" "}
                        para considerarla liquidada.
                    </Caption1>
                )}
            </div>
        );
    };

    const renderComments = () => {
        if (!debt) return null;
        return (
            <div className={styles.sectionCard}>
                <div className={styles.sectionTitle}>
                    <Comment20Regular />
                    <Subtitle2>Comentarios</Subtitle2>
                </div>
                {debt.comments && debt.comments.trim().length > 0 ? (
                    <div className={styles.commentsBox}>{debt.comments}</div>
                ) : (
                    <span className={styles.emptyText}>Sin comentarios.</span>
                )}
            </div>
        );
    };

    return (
        <OverlayDrawer
            position="end"
            open={props.isOpen}
            onOpenChange={(_e, data) => {
                if (!data.open) props.onClose();
            }}
            size="medium"
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            icon={<Dismiss24Regular />}
                            onClick={props.onClose}
                            aria-label="Cerrar"
                        />
                    }
                >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <DocumentTextRegular />
                        <Title3 as="span">Detalle de deuda</Title3>
                    </span>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                {isLoading && (
                    <div className={styles.loadingWrap}>
                        <Spinner label="Cargando deuda…" />
                    </div>
                )}
                {!isLoading && !debt && (
                    <div className={styles.loadingWrap}>
                        <Body1>No se encontró la deuda seleccionada.</Body1>
                    </div>
                )}
                {!isLoading && debt && (
                    <div className={styles.drawerBody}>
                        {renderHeader()}
                        {renderParties()}
                        {renderAmounts()}
                        {renderProtocol()}
                        {renderSettlement()}
                        <Divider />
                        {renderComments()}
                        <Caption1
                            style={{
                                color: tokens.colorNeutralForeground3,
                                textAlign: "right",
                            }}
                        >
                            Identificador: {debt.id}
                        </Caption1>
                    </div>
                )}
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(DebtDetailsComponent as any) as React.FC<{
    DebtId: number | undefined;
    isOpen: boolean;
    onClose: () => void;
}>;
